import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Id } from "@cvx/_generated/dataModel";
import { Link } from "@tanstack/react-router";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { formatCurrency } from "@/utils/format";
import { useEffect } from "react";
import { usePaymentMethods } from "@/hooks";
import { useQuery } from "@tanstack/react-query";

interface Contract {
  _id: Id<"creatorContracts">;
  creator?: { name: string };
  campaign?: { name: string };
  currency?: "usd" | "eur" | "gbp";
  earnings?: {
    earningsFlatFee: number;
    earningsPerVideoBonus: number;
    earningsCPM: number;
  };
  creatorAmount?: number;
  platformFee?: number;
  totalAmountWithFee?: number;
}

interface PayAllModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContracts: Set<Id<"creatorContracts">>;
  selectedContractsList: Contract[];
  totalAmount: number;
  currency: "usd" | "eur" | "gbp";
  onPayAll: () => void;
  isProcessing?: boolean;
}

export function PayAllModal({
  isOpen,
  onClose,
  selectedContracts,
  selectedContractsList,
  totalAmount,
  currency,
  onPayAll,
  isProcessing = false,
}: PayAllModalProps) {
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const {
    paymentMethods,
    hasPaymentMethod,
    isLoading: isLoadingPaymentMethods,
    checkPaymentMethods,
    formatCardBrand,
  } = usePaymentMethods();

  useEffect(() => {
    if (user?._id && isOpen) {
      checkPaymentMethods(user._id);
    }
  }, [user?._id, isOpen, checkPaymentMethods]);

  // Calculate total creator amount and platform fees
  const totalCreatorAmount = selectedContractsList.reduce(
    (sum, contract) => sum + (contract.creatorAmount || 0),
    0,
  );
  const totalPlatformFee = selectedContractsList.reduce(
    (sum, contract) => sum + (contract.platformFee || 0),
    0,
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isProcessing) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Pay Multiple Contracts</DialogTitle>
          <DialogDescription>
            Process batch payments for selected creator contracts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto flex-1 px-1 scrollbar-hide">
          {/* Payment Summary */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-primary">
                Total Amount:
              </span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(totalAmount, 2, currency)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-green-600 font-medium">
                Creator payments:
              </span>
              <span className="text-green-600 font-medium">
                {formatCurrency(totalCreatorAmount, 2, currency)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-blue-600 font-medium">
                Platform fees (20%):
              </span>
              <span className="text-blue-600 font-medium">
                {formatCurrency(totalPlatformFee, 2, currency)}
              </span>
            </div>
            <div className="flex justify-between items-center border-t pt-2">
              <span className="text-sm text-muted-foreground">
                Number of payments:
              </span>
              <span className="text-sm font-medium">
                {selectedContracts.size} payment
                {selectedContracts.size !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* Contract Details Table */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-primary">
              Payment Breakdown:
            </h4>
            <div className="border rounded-lg max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-primary">
                      Creator
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-primary">
                      Campaign
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-primary">
                      Creator
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-primary">
                      Platform Fee
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-primary">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {selectedContractsList.map((contract) => {
                    return (
                      <tr key={contract._id} className="hover:bg-muted/20">
                        <td className="px-4 py-3">
                          <span className="font-medium">
                            {contract.creator?.name || "Unknown Creator"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {contract.campaign?.name || "Unknown Campaign"}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600 font-medium">
                          {formatCurrency(
                            contract.creatorAmount || 0,
                            2,
                            contract.currency,
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-600 font-medium">
                          {formatCurrency(
                            contract.platformFee || 0,
                            2,
                            contract.currency,
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatCurrency(
                            contract.totalAmountWithFee || 0,
                            2,
                            contract.currency,
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Method Section */}
          {isLoadingPaymentMethods ? (
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Loading payment information...
              </p>
            </div>
          ) : hasPaymentMethod && paymentMethods.length > 0 ? (
            <>
              {/* Show Payment Method */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-primary">
                  Payment Method:
                </h4>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-6 bg-gray-800 rounded text-white text-xs flex items-center justify-center font-bold">
                        {formatCardBrand(paymentMethods[0].brand)}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          •••• •••• •••• {paymentMethods[0].last4}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Expires{" "}
                          {paymentMethods[0].expMonth
                            ?.toString()
                            .padStart(2, "0")}
                          /{paymentMethods[0].expYear}
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/dashboard/settings/billing"
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Change
                    </Link>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg space-y-2">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
                  💳 Payment Process
                </h4>
                <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <p>
                    • Your card will be charged{" "}
                    <strong>{selectedContracts.size} separate times</strong>{" "}
                    (once per creator)
                  </p>
                  <p>
                    • Each charge includes the creator payment + 20% platform
                    fee
                  </p>
                  <p>
                    • Payment receipts will be{" "}
                    <strong>automatically uploaded</strong> to each contract
                  </p>
                  <p>
                    • All payments will be processed immediately upon
                    confirmation
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* No Payment Method */}
              <div className="bg-amber-50 dark:bg-amber-950/20 p-6 rounded-lg text-center space-y-4">
                <div className="text-amber-800 dark:text-amber-200">
                  <div className="text-2xl mb-2">💳</div>
                  <h4 className="text-lg font-medium mb-2">
                    Payment Method Required
                  </h4>
                  <p className="text-sm mb-4">
                    You need to add a payment method to your account before you
                    can process payments.
                  </p>
                  <Link
                    to="/dashboard/settings/billing"
                    className="inline-flex items-center justify-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-medium transition-colors"
                  >
                    Add Payment Method
                  </Link>
                </div>
              </div>
            </>
          )}

          {/* Warning */}
          {hasPaymentMethod && (
            <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                ⚠️ <strong>Important:</strong> This action cannot be undone.
                Please review all payment details carefully before proceeding.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 flex-shrink-0 mt-4">
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          {hasPaymentMethod ? (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={onPayAll}
              disabled={isLoadingPaymentMethods || isProcessing}
            >
              {isProcessing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Processing...
                </>
              ) : (
                <>
                  Process {selectedContracts.size} Payment
                  {selectedContracts.size !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          ) : (
            <Link to="/dashboard/settings/billing">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isLoadingPaymentMethods || isProcessing}
              >
                Add Payment Method
              </Button>
            </Link>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
